// ============================================================================
// Sub-replacement CUTOVER modal. Shown on an Approved request for approvers.
// Confirmation-FIRST (AFB-0029): everything already captured on the request
// (incoming sub, No-Miss / Compliment config) renders as a read-only summary
// — one click to confirm. Only genuinely missing values prompt for input;
// an "Edit terms" toggle exposes the full form for day-of-change overrides.
// Confirm → fn_generate... no: → sub_replacement_cutover RPC, which mints the
// incoming assignment (carrying billing/rate/role forward), ends the outgoing,
// links the lineage, and Completes the request.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { toNumOrNull } from '../../shared/coerce';
import { SmartSelect } from '../../components/smart-select';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import { commitWrite } from '../../shared/save-path';
import { fmtNight, localIsoDate, nightBefore } from './nights';
import { HandoverNote, cutoverReady } from './handover-note';
import { paysSubByTheHour, perHour } from '../../shared/rate-basis';
import { hourlyMonthNote, payUnit, type PayLine } from './pay-unit';

interface Props {
  requestId: string;
  facilityId: string | null;
  onClose: () => void;
  onDone: (newAssignmentId: string) => void;
}

const numOrNull = (s: string): number | null => toNumOrNull(s);


export function CutoverModal({ requestId, facilityId, onClose, onDone }: Props) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [incomingProviderId, setIncomingProviderId] = useState<string | null>(null);
  const [nmiEnabled, setNmiEnabled] = useState(false);
  const [nmiAmount, setNmiAmount] = useState('');
  const [orCount, setOrCount] = useState('');
  const [prCount, setPrCount] = useState('');
  const [complimentEnabled, setComplimentEnabled] = useState(false);
  const [crewSize, setCrewSize] = useState('');
  // 2026-08-21 — the incoming sub's monthly pay. REQUIRED AT CUTOVER, not at
  // submit (Ben's ruling): a request may open without a rate, because 53 of the
  // 80 historical requests carried none at submit and blocking intake is how an
  // AM learns to route around the system. It may not COMPLETE without one.
  //
  // The rate mattering here is not cosmetic. `sub_replacement_cutover` used to
  // read `COALESCE(request.incoming_pay_rate, outgoing.sub_rate_monthly)`, so a
  // NULL rate silently paid the NEW sub the OLD sub's number — which is what
  // happened on 50 of the 77 completed requests, and why the AM ends up telling
  // Jose the real rate in a free-text Billing Comms message instead.
  const [payRate, setPayRate] = useState('');
  // The outgoing assignment's current rate, shown for contrast so the approver
  // can see what is changing. Never used as a fallback value.
  const [outgoingRate, setOutgoingRate] = useState<number | null>(null);
  // Item 896: the outgoing line decides the pay's unit. On a line that pays its
  // sub by the hour, outgoingRate is the hourly rate and the box asks per hour.
  const [outgoingLine, setOutgoingLine] = useState<PayLine | null>(null);
  const hourly = paysSubByTheHour(outgoingLine);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Confirmation-first state: what the request already captured, and whether
  // the approver explicitly chose to override it.
  const [requestHasIncentives, setRequestHasIncentives] = useState(false);
  const [incomingName, setIncomingName] = useState<string | null>(null);
  const [incomingStatus, setIncomingStatus] = useState<string | null>(null);
  const [editTerms, setEditTerms] = useState(false);
  // ONE DATE IS ASKED (TCSD-0336): the incoming sub's first night. The outgoing
  // sub's last night is always the night before it — shown, never asked, never
  // blank. A handover is a calendar boundary, so how many nights a week the
  // building is cleaned has no bearing on it.
  const [outgoingName, setOutgoingName] = useState<string | null>(null);
  const [outgoingStart, setOutgoingStart] = useState<string | null>(null);
  const [firstNight, setFirstNight] = useState('');
  // What the request already holds, so the first night is written only when the
  // person changed it.
  const [savedFirstNight, setSavedFirstNight] = useState<string | null>(null);
  // The sub the outgoing sub replaced on this same service line. Choosing them
  // as the incoming sub undoes a mistaken change (TCSD-0336 point 3). This is
  // only what the window SAYS; the database decides, and refuses what it cannot
  // put right.
  const [previousSubId, setPreviousSubId] = useState<string | null>(null);
  const [previousSubName, setPreviousSubName] = useState<string | null>(null);
  // What the person reads after it lands. Kept in the modal rather than handed
  // to the caller so the AM sees it without a screen change.
  const [done, setDone] = useState<string | null>(null);
  const [lastAssignmentId, setLastAssignmentId] = useState('');

  // Prefill: incoming sub from the request, config from the outgoing assignment.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data: srr, error: e1 } = await supabase
          .from('sub_replacement_requests')
          .select(
            'outgoing_assignment_id, incoming_provider_id, incoming_pay_rate, nmi_enabled, nmi_amount, compliment_enabled, compliment_amount, planned_change_date',
          )
          .eq('request_id', requestId)
          .maybeSingle();
        if (e1) throw e1;
        if (cancelled) return;
        const srrRow = srr as {
          outgoing_assignment_id: string | null;
          incoming_provider_id: string | null;
          incoming_pay_rate: number | null;
          nmi_enabled: boolean | null;
          nmi_amount: number | null;
          compliment_enabled: boolean | null;
          compliment_amount: number | null;
          planned_change_date: string | null;
        } | null;
        setFirstNight(srrRow?.planned_change_date ?? '');
        setSavedFirstNight(srrRow?.planned_change_date ?? null);
        setIncomingProviderId(srrRow?.incoming_provider_id ?? null);
        setPayRate(srrRow?.incoming_pay_rate != null ? String(srrRow.incoming_pay_rate) : '');
        // Resolve the incoming sub's name + status so the confirm screen can
        // show "Adriana Ramirez" instead of a picker, and warn early when the
        // provider isn't Active yet (the RPC refuses non-Active providers).
        if (srrRow?.incoming_provider_id) {
          const { data: sp } = await supabase
            .from('service_providers')
            .select('partner_name, provider_status')
            .eq('provider_id', srrRow.incoming_provider_id)
            .maybeSingle();
          if (cancelled) return;
          const spRow = sp as {
            partner_name: string | null;
            provider_status: string | null;
          } | null;
          setIncomingName(spRow?.partner_name ?? srrRow.incoming_provider_id);
          setIncomingStatus(spRow?.provider_status ?? null);
        }
        // AFB-0027: values captured on the request itself win over the
        // outgoing assignment's config — the AM/approver may have already
        // adjusted them. Fall back to the outgoing assignment when unset.
        const requestHasIncentives =
          srrRow != null &&
          (srrRow.nmi_enabled != null || srrRow.compliment_enabled != null);
        setRequestHasIncentives(requestHasIncentives);
        if (requestHasIncentives && srrRow) {
          setNmiEnabled(Boolean(srrRow.nmi_enabled));
          setNmiAmount(srrRow.nmi_amount != null ? String(srrRow.nmi_amount) : '');
          setComplimentEnabled(Boolean(srrRow.compliment_enabled));
        }
        const outId = srrRow?.outgoing_assignment_id ?? null;
        if (outId) {
          const { data: a, error: e2 } = await supabase
            .from('service_assignments')
            .select(
              'no_misses_bonus_enabled, no_misses_bonus_amount, or_count, pr_count, compliment_incentive_enabled, crew_size, sub_rate_monthly, sub_rate_type, sub_hourly_rate, sub_hours_per_day, service_days_per_week, start_date, provider_id, facility_id, service_code',
            )
            .eq('assignment_id', outId)
            .maybeSingle();
          if (e2) throw e2;
          if (cancelled) return;
          const cfg = a as {
            no_misses_bonus_enabled: boolean | null;
            no_misses_bonus_amount: number | null;
            or_count: number | null;
            pr_count: number | null;
            compliment_incentive_enabled: boolean | null;
            crew_size: number | null;
            sub_rate_monthly: number | null;
            sub_rate_type: string | null;
            sub_hourly_rate: number | null;
            sub_hours_per_day: number | null;
            service_days_per_week: number | null;
            start_date: string | null;
            provider_id: string | null;
            facility_id: string | null;
            service_code: string | null;
          } | null;
          setOutgoingStart(cfg?.start_date ?? null);
          if (cfg?.provider_id && cfg.facility_id) {
            // Who the outgoing sub replaced on this same service line: the most
            // recent completed change that brought them in. Informational only —
            // if this read fails the window simply does not announce an undo, and
            // the database still recognises one.
            const { data: prevReq, error: ePrev } = await supabase
              .from('sub_replacement_requests')
              .select('outgoing_assignment_id')
              .eq('facility_id', cfg.facility_id)
              .eq('status', 'Completed')
              .eq('incoming_provider_id', cfg.provider_id)
              .neq('request_id', requestId)
              .not('outgoing_assignment_id', 'is', null)
              .order('updated_at', { ascending: false })
              .limit(1);
            if (cancelled) return;
            const prevLineId = !ePrev
              ? (prevReq as { outgoing_assignment_id: string | null }[] | null)?.[0]
                  ?.outgoing_assignment_id ?? null
              : null;
            if (prevLineId) {
              const { data: pl } = await supabase
                .from('service_assignments')
                .select('provider_id, service_code')
                .eq('assignment_id', prevLineId)
                .maybeSingle();
              if (cancelled) return;
              const plRow = pl as { provider_id: string | null; service_code: string | null } | null;
              if (plRow?.provider_id && plRow.service_code === cfg.service_code) {
                const { data: pp } = await supabase
                  .from('service_providers')
                  .select('partner_name')
                  .eq('provider_id', plRow.provider_id)
                  .maybeSingle();
                if (cancelled) return;
                setPreviousSubId(plRow.provider_id);
                setPreviousSubName(
                  (pp as { partner_name: string | null } | null)?.partner_name ?? plRow.provider_id,
                );
              }
            }
          }
          if (cfg?.provider_id) {
            const { data: op } = await supabase
              .from('service_providers')
              .select('partner_name')
              .eq('provider_id', cfg.provider_id)
              .maybeSingle();
            if (cancelled) return;
            setOutgoingName(
              (op as { partner_name: string | null } | null)?.partner_name ?? cfg.provider_id,
            );
          }
          if (cfg) {
            // Shown for contrast only. This is the number that used to be
            // silently copied onto the incoming sub when the rate was blank.
            // On an hourly line it is the hourly rate (item 896).
            setOutgoingLine({
              sub_rate_type: cfg.sub_rate_type,
              sub_hours_per_day: cfg.sub_hours_per_day,
              service_days_per_week: cfg.service_days_per_week,
            });
            setOutgoingRate(
              paysSubByTheHour(cfg) ? cfg.sub_hourly_rate : cfg.sub_rate_monthly,
            );
            // Enabled/amount toggles: only fall back to the outgoing
            // assignment when the request didn't capture them (AFB-0027).
            if (!requestHasIncentives) {
              setNmiEnabled(Boolean(cfg.no_misses_bonus_enabled));
              setNmiAmount(cfg.no_misses_bonus_amount != null ? String(cfg.no_misses_bonus_amount) : '');
              setComplimentEnabled(Boolean(cfg.compliment_incentive_enabled));
            }
            // Structural counts always come from the outgoing assignment.
            setOrCount(cfg.or_count != null ? String(cfg.or_count) : '');
            setPrCount(cfg.pr_count != null ? String(cfg.pr_count) : '');
            setCrewSize(cfg.crew_size != null ? String(cfg.crew_size) : '');
          }
        }
      } catch (err) {
        if (!cancelled)
          setLoadError(
            errorMessage(err, 'Could not load the outgoing assignment.'),
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  // Parsed once. `toNumOrNull` is the shared coercion, so '' and a non-number
  // both land on null and the one gate below covers both.
  const rateValue = numOrNull(payRate);

  const today = localIsoDate(new Date());
  const lastNight = firstNight ? nightBefore(firstNight) : null;
  const startsLater = firstNight !== '' && firstNight > today;
  const startedAlready = firstNight !== '' && firstNight < today;
  // Choosing the sub the outgoing one replaced undoes a mistaken change.
  const restating = previousSubId != null && incomingProviderId === previousSubId;
  // …and whether the mistaken sub cleaned any nights decides what happens to
  // their line. Unknown when their first night is not on file.
  const restateCleanedNone =
    restating && outgoingStart != null && firstNight !== '' && outgoingStart >= firstNight;

  // What the person reads when it lands. One line each, and every one ends with
  // what they still owe — which is usually nothing. Ben, 2026-09-08: the
  // sentences were right but too long; account managers will not read a
  // paragraph.
  function confirmationText(): string {
    const inn = incomingName ?? 'the new sub';
    const out = outgoingName ?? 'the previous sub';
    const start = fmtNight(firstNight);
    const last = fmtNight(lastNight);

    if (restating && restateCleanedNone) {
      return `Put back. ${inn} carries on as if the change never happened, and ${out}'s line is removed. Nothing else needed.`;
    }
    if (restating) {
      return `Put back. ${inn} is back from ${start}; ${out} keeps the nights they cleaned, through ${last}. Nothing else needed.`;
    }
    if (startsLater) {
      return `Booked for ${start}. ${out} works through ${last}. Switches over on its own. Nothing else needed.`;
    }
    if (startedAlready) {
      return `Recorded. ${out} out ${last}, ${inn} in ${start}. Finance notified. Nothing else needed.`;
    }
    return `Recorded. ${inn} starts tonight; ${out}'s last night was ${last}. Finance notified. Nothing else needed.`;
  }

  async function confirm() {
    if (!incomingProviderId) {
      setError('Pick the incoming sub first.');
      return;
    }
    if (nmiAmount.trim() !== '' && Number.isNaN(Number(nmiAmount))) {
      setError('Scope Performance Payment amount must be a number.');
      return;
    }
    // The rate is required HERE and nowhere earlier. The database refuses the
    // same thing (a BEFORE trigger on the transition into Completed), so this
    // check is the sentence the approver reads, not the enforcement.
    if (rateValue === null) {
      setError(
        `Enter the incoming subcontractor's contract rate per ${hourly ? 'hour' : 'month'}. The cutover cannot complete without it — it is the number Finance pays from.`,
      );
      return;
    }
    if (!firstNight) {
      setError(`Choose ${incomingName ?? 'the new sub'}'s first night.`);
      return;
    }
    setBusy(true);
    const startedAt = new Date(Date.now() - 5000).toISOString();
    setError(null);
    try {
      // The first night lives on the request, which is where the database reads
      // it. Written only when the person changed it, and proven to have landed
      // before the change is recorded against it.
      if (firstNight !== savedFirstNight) {
        await commitWrite({
          label: 'record the first night',
          write: supabase
            .from('sub_replacement_requests')
            .update({ planned_change_date: firstNight })
            .eq('request_id', requestId)
            .select('request_id'),
          touches: ['sub_replacement_requests'],
        });
        setSavedFirstNight(firstNight);
      }
      // 2026-08-21 — the A2 half of this TODO is CLOSED. `p_incoming_pay_rate`
      // is written onto the request and onto the incoming assignment's
      // `sub_rate_monthly`, and the cutover emits `sub_rate_changed` to role
      // finance when the number actually changed. Downstream propagation into
      // `client_billing_lines` / invoicing remains Jose's design loop, and
      // day-of-change notifications are still A3 (Option C deep-link build) —
      // neither is wired here.
      const { data, error: e } = await supabase.rpc('sub_replacement_cutover', {
        p_request_id: requestId,
        p_incoming_provider_id: incomingProviderId,
        p_incoming_pay_rate: rateValue,
        p_nmi_enabled: nmiEnabled,
        p_nmi_amount: numOrNull(nmiAmount),
        p_or_count: numOrNull(orCount),
        p_pr_count: numOrNull(prCount),
        p_compliment_enabled: complimentEnabled,
        p_crew_size: numOrNull(crewSize),
        // No last night is sent. The database works it out — the night before
        // the first night — and refuses one that disagrees (TCSD-0336).
      });
      if (e) throw e;
      // HR's onboarding card is made by the database at a sub's FIRST building
      // (TCSD-0369). Say so only when it actually happened — read it back.
      let hrLine = '';
      if (incomingProviderId) {
        const { data: card } = await supabase
          .from('partner_onboarding')
          .select('onboarding_id, assigned_to_eid')
          .eq('provider_id', incomingProviderId)
          .gte('created_at', startedAt)
          .limit(1)
          .maybeSingle();
        const c = card as { onboarding_id: string; assigned_to_eid: string | null } | null;
        if (c) {
          let hr = 'HR';
          if (c.assigned_to_eid) {
            const { data: emp } = await supabase.from('employees').select('employee_name_first').eq('employee_id', c.assigned_to_eid).maybeSingle();
            hr = (emp as { employee_name_first?: string } | null)?.employee_name_first ?? 'HR';
          }
          hrLine = ` ${hr} has been notified to start ${incomingName ?? 'the new sub'}'s setup paperwork.`;
        }
      }
      setDone(confirmationText() + hrLine);
      setLastAssignmentId(typeof data === 'string' ? data : String(data ?? ''));
    } catch (err) {
      // PostgrestError is a plain object, NOT an Error instance — surfacing
      // the real message is the AFB-0029 fix (it used to always show the
      // generic fallback).
      setError(rpcErrorMessage(err, 'Cutover failed.'));
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    'mt-1 block w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm focus:border-tcs-border-input focus:outline-none';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-lg bg-tcs-overlay p-5 shadow-xl"
      >
        <h2 className="text-lg font-semibold text-tcs-text">Record the sub change</h2>
        <p className="mt-1 text-xs text-tcs-text-muted">
          One date: the night the new sub starts. The previous sub&rsquo;s last night is
          always the night before, so two subs never clean the same night and no night
          is left with nobody. A change that already happened, one happening tonight
          and one booked for later all go through this same step — a date in the past
          is normal and is never questioned. Everything else below defaults to what the
          request captured.
        </p>

        {loadError && (
          <div className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-sm text-st-danger-fg-strong">
            {loadError}
          </div>
        )}

        {done && (
          // What landed, in one line, ending with what they still owe. Shown
          // here rather than as a toast so it does not vanish before it is read.
          <div className="mt-3 rounded-md border border-st-ok-border bg-st-ok-bg p-3">
            <p className="text-sm font-medium text-st-ok-fg-strong">{done}</p>
            <button
              type="button"
              onClick={() => onDone(lastAssignmentId)}
              className="mt-3 rounded-md bg-tcs-inverse px-4 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover"
            >
              Done
            </button>
          </div>
        )}

        {done ? null : loading ? (
          <p className="mt-4 text-sm text-tcs-text-muted">Loading current terms…</p>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="block text-xs">
              <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
                Incoming sub
              </span>
              {incomingProviderId && incomingName && !editTerms ? (
                // Already captured on the request — confirm, don't re-ask.
                <div className="flex items-center justify-between rounded-md border border-tcs-border bg-tcs-raised px-3 py-2">
                  <span className="text-sm font-medium text-tcs-text">
                    {incomingName}
                  </span>
                  {incomingStatus && incomingStatus !== 'Active' && (
                    <span className="rounded bg-st-warn-bg-strong px-2 py-0.5 text-[11px] font-medium text-st-warn-fg-strong">
                      {incomingStatus} — cutover will proceed; finishing onboarding
                      becomes an Urgent task in the AM&rsquo;s My Work
                    </span>
                  )}
                </div>
              ) : (
                <SmartSelect
                  entity="service_provider"
                  scope={facilityId ? { facility_id: facilityId } : undefined}
                  value={incomingProviderId}
                  onChange={(id) => setIncomingProviderId(id || null)}
                  placeholder="Search subs in this market…"
                  entityLabel="sub"
                />
              )}
            </div>

            {/* Sub pay. Required at cutover — the one field that decides what
                Finance pays the incoming sub, and the reason a roster change
                used to need a Billing Comms message to carry it. */}
            <label className="block text-xs">
              <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
                Incoming contract rate ({payUnit(outgoingLine)}) <span className="text-st-danger-fg">*</span>
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={payRate}
                onChange={(e) => setPayRate(e.target.value)}
                placeholder={hourly ? 'The incoming subcontractor’s contract rate per hour' : 'The incoming subcontractor’s contract rate per month'}
                className={inputCls}
                aria-invalid={rateValue === null}
              />
              <span className="mt-1 block text-[11px] text-tcs-text-muted">
                {outgoingRate != null
                  ? hourly
                    ? `The outgoing subcontractor's contract rate was ${perHour(Number(outgoingRate))}. Enter the incoming subcontractor's own contract rate — it is not carried over.`
                    : `The outgoing subcontractor's contract rate was $${outgoingRate.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}/mo. Enter the incoming subcontractor's own contract rate — it is not carried over.`
                  : 'Finance is notified of this number when the cutover completes.'}
              </span>
              {hourlyMonthNote(rateValue, outgoingLine) && (
                <span className="mt-1 block text-[11px] text-tcs-text-muted">
                  {hourlyMonthNote(rateValue, outgoingLine)}
                </span>
              )}
            </label>

            {/* ONE DATE (TCSD-0336). The incoming sub's first night is asked; the
                outgoing sub's last night is the night before it, shown here in
                plain words and never asked. Save stays off until it is chosen. */}
            <label className="block text-xs">
              <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
                {incomingName ? `${incomingName}'s first night` : "Incoming sub's first night"}{' '}
                <span className="text-st-danger-fg">*</span>
              </span>
              <input
                type="date"
                value={firstNight}
                onChange={(e) => setFirstNight(e.target.value)}
                className={inputCls}
                aria-invalid={firstNight === ''}
                data-testid="cutover-first-night"
              />
            </label>
            <HandoverNote
              outgoingName={outgoingName}
              firstNight={firstNight}
              restate={restating ? { previousSubName, outgoingStart } : null}
            />

            {requestHasIncentives && !editTerms ? (
              // Terms were captured on the approved request — show them
              // read-only with a one-click confirm; edit only on demand.
              <div className="rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                    Performance payment terms (from the approved request)
                  </p>
                  <button
                    type="button"
                    onClick={() => setEditTerms(true)}
                    className="text-xs font-medium text-tcs-text-secondary underline hover:text-tcs-text"
                  >
                    Edit terms
                  </button>
                </div>
                <p className="mt-2 text-sm text-tcs-text">
                  Scope Performance:{' '}
                  {nmiEnabled
                    ? `on${nmiAmount ? ` — $${nmiAmount}/mo` : ''}${
                        orCount || prCount
                          ? ` (OR ${orCount || 0} / PR ${prCount || 0})`
                          : ''
                      }`
                    : 'off'}
                  {' · '}Compliment:{' '}
                  {complimentEnabled
                    ? `on${crewSize ? ` — crew of ${crewSize}` : ''}`
                    : 'off'}
                </p>
              </div>
            ) : (
            <div className="rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-3 space-y-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                Performance payment terms (carried forward — edit if they change)
              </p>
              <label className="flex items-center gap-2 text-sm text-tcs-text-secondary">
                <input
                  type="checkbox"
                  checked={nmiEnabled}
                  onChange={(e) => setNmiEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-tcs-border"
                />
                Scope Performance Payment enabled
              </label>
              {nmiEnabled && (
                <div className="grid grid-cols-3 gap-2">
                  <label className="block text-xs text-tcs-text-muted">
                    Amount ($)
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={nmiAmount}
                      onChange={(e) => setNmiAmount(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-xs text-tcs-text-muted">
                    OR count
                    <input
                      type="number"
                      min="0"
                      value={orCount}
                      onChange={(e) => setOrCount(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-xs text-tcs-text-muted">
                    PR count
                    <input
                      type="number"
                      min="0"
                      value={prCount}
                      onChange={(e) => setPrCount(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-tcs-text-secondary">
                <input
                  type="checkbox"
                  checked={complimentEnabled}
                  onChange={(e) => setComplimentEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-tcs-border"
                />
                Compliment payment enabled
              </label>
              {complimentEnabled && (
                <label className="block text-xs text-tcs-text-muted">
                  Crew size (gift cards = crew + 1 leader)
                  <input
                    type="number"
                    min="0"
                    value={crewSize}
                    onChange={(e) => setCrewSize(e.target.value)}
                    className={`${inputCls} max-w-[8rem]`}
                  />
                </label>
              )}
            </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-sm text-st-danger-fg-strong">
            {error}
          </div>
        )}

        <div className={`mt-5 flex items-center justify-end gap-2${done ? ' hidden' : ''}`}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-tcs-border px-3 py-1.5 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!cutoverReady({ busy, loading, incomingProviderId, firstNight })}
            data-testid="cutover-save"
            className="rounded-md bg-tcs-inverse px-4 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
          >
            {busy
              ? 'Saving…'
              : restating
                ? `Put ${previousSubName ?? 'them'} back`
                : startsLater
                  ? 'Schedule the change'
                  : 'Record the change'}
          </button>
        </div>
      </div>
    </div>
  );
}
